import React, { useContext, useEffect, useState } from 'react';
import { StoreContext } from '../../Library/contexts';
import { useNavigate } from 'react-router-dom';
import { Input, Button, Spinner } from '@chakra-ui/react';
import { getLatestRouteTrip } from '../../APIs';
import PATHS from '../../Routes/paths';
import { SearchIcon } from '../../Assets/images/svgs';
import PinInputField from './PinInputField';

const ROUTE_TRIP_CREATED = 1;
const PIN_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

// ─── Switch Driver Modal ──────────────────────────────────────────────────────

const SwitchDriverModal = ({ onClose, onSwitched }) => {
  const { setActiveDriver, setDriverPinEnteredAt } = useContext(StoreContext);

  const handleVerified = (driver) => {
    setActiveDriver(driver);
    setDriverPinEnteredAt(Date.now());
    onSwitched(driver);
  };

  return (
    <div className="df-pin-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="df-pin-modal-card">
        <button className="df-pin-close-btn" onClick={onClose} aria-label="Close">✕</button>
        <PinInputField
          onVerified={handleVerified}
          title="Switch Driver"
          subtitle="Enter the new driver's 4-digit PIN."
        />
      </div>
    </div>
  );
};

// ─── Select Bus Page ──────────────────────────────────────────────────────────

const SelectBus = () => {
  const { userVehicles, activeDriver, driverPinEnteredAt } = useContext(StoreContext);
  const [search, setSearch] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  // Track a set of all currently-busy vehicle IDs so multiple concurrent trips
  // are each reflected correctly in the list.
  const [busyVids, setBusyVids] = useState(new Set());
  const [loadingTrip, setLoadingTrip] = useState(true);
  const [showSwitchDriver, setShowSwitchDriver] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch all active trips for this account so we can mark every busy vehicle,
    // not just the most-recently-started one.
    getLatestRouteTrip({ forAccount: true }, (err, data) => {
      setLoadingTrip(false);
      if (!err && data) {
        // API returns an array when forAccount=true, a single object otherwise.
        const trips = Array.isArray(data) ? data : [data];
        const busy = new Set(
          trips
            .filter(t => !t.endTs && t.status === ROUTE_TRIP_CREATED)
            .map(t => t.vid)
        );
        setBusyVids(busy);
      }
    });
  }, []);

  const isWithin15Min = () =>
    driverPinEnteredAt && (Date.now() - driverPinEnteredAt) < PIN_THRESHOLD_MS;

  const filteredVehicles = (userVehicles || []).filter(v =>
    v.reg?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (vehicle) => {
    navigate(`../${PATHS.SELECT_ROUTE}`, { state: { vehicle } });
  };

  const handleDriverSwitched = () => {
    setShowSwitchDriver(false);
  };

  return (
    <div className="df-page df-select-bus">
      <div className="df-header">
        <h1>Select a Bus</h1>
        <p>Select a bus to start your route</p>
      </div>

      {/* Logged-in driver banner */}
      {activeDriver && isWithin15Min() && (
        <div className="df-logged-in-banner">
          <span className="df-logged-in-label">Logged in as <strong>{activeDriver.nm}</strong></span>
          <button className="df-switch-driver-btn" onClick={() => setShowSwitchDriver(true)}>
            Switch Driver
          </button>
        </div>
      )}

      <div className="df-search-input-wrapper">
        <SearchIcon className="df-search-icon" />
        <Input
          className="df-search-input"
          placeholder="Search Bus Registration Number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loadingTrip ? (
        <div className="df-loader"><Spinner color="var(--ButtonPrimary)" /></div>
      ) : (
        <div className="df-list-container">
          {filteredVehicles.map(veh => {
            const isBusy = busyVids.has(veh.idx) || busyVids.has(veh.vid);
            return (
              <div
                key={veh.idx}
                className={`df-vehicle-card${selectedVehicle?.idx === veh.idx ? " selected" : ""}${isBusy ? " df-vehicle-busy" : ""}`}
                onClick={() => !isBusy && setSelectedVehicle(veh)}
              >
                <div className="df-vehicle-icon-box">
                  <img src="https://img.ajjas.com/images/web_assets/HEAVY_VEH_NORMAL.png" alt="Bus" />
                </div>
                <div className="df-vehicle-info">
                  <span className="df-veh-reg">{veh.reg}</span>
                  <span className="df-veh-type">
                    <span>|</span> {veh.model || "Big Bus"} <span>|</span> {veh.fuelType || "Diesel"}
                  </span>
                </div>
                {isBusy && <span className="df-busy-badge">Trip in Progress</span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="df-footer-btn-container">
        <Button
          className="df-action-btn"
          isDisabled={!selectedVehicle}
          onClick={() => { if (selectedVehicle) handleSelect(selectedVehicle); }}
        >
          Continue
        </Button>
      </div>

      {showSwitchDriver && (
        <SwitchDriverModal
          onClose={() => setShowSwitchDriver(false)}
          onSwitched={handleDriverSwitched}
        />
      )}
    </div>
  );
};

export default SelectBus;
